/**
 * Temporary alarm suppression ("mute") for a device.
 *
 * State lives in one nullable column, `core_device.away_until`: a UTC timestamp
 * `'YYYY-MM-DD HH:MM:SS'` for when suppression ends, or null/empty for "not
 * muted". A mute therefore expires on its own with no scheduled job — every
 * reader just compares the stored instant to now.
 *
 * `nowMs` is injectable on both functions. That is what makes the boundary
 * behaviour testable: "one second before expiry" and "one second after" are the
 * cases worth being sure about, and they cannot be exercised against a live clock.
 *
 * Formatting for display is deliberately NOT here — that is
 * `convertIsoUTCToLocal(awayUntil, tzOffset)` in libs.jsx, the app's existing
 * UTC→campus-local formatter. Reimplementing it would let this banner disagree
 * with every other localized timestamp in the portal.
 */

import { DateTime } from 'luxon';

/** Wire format for `away_until`. Matches getLuxonUTCTime() in libs.jsx. */
const SQL_UTC = 'yyyy-MM-dd HH:mm:ss';

/**
 * Selectable suppression windows, in the order they appear in the dropdown.
 *
 * Minutes rather than seconds: the payload is a timestamp, so nothing downstream
 * needs a duration, and minutes keep the arithmetic readable at the call site.
 */
export const MUTE_DURATIONS = [
  { minutes: 30, label: '30 minutes' },
  { minutes: 60, label: '1 hour' },
  { minutes: 120, label: '2 hours' },
  { minutes: 240, label: '4 hours' },
  { minutes: 480, label: '8 hours' },
  { minutes: 1440, label: '24 hours' },
];

/** The window the dropdown starts on. */
export const DEFAULT_MUTE_MINUTES = MUTE_DURATIONS[0].minutes;

/**
 * The `away_until` value to write for a mute of `minutes` starting now.
 *
 * @param {number} minutes
 * @param {number} [nowMs] — epoch ms; injectable for tests
 * @returns {string} UTC 'YYYY-MM-DD HH:MM:SS', or '' if minutes is unusable
 */
export function muteUntilTimestamp(minutes, nowMs = Date.now()) {
  const mins = Number(minutes);
  if (!Number.isFinite(mins) || mins <= 0) return '';
  return DateTime.fromMillis(nowMs, { zone: 'utc' })
    .plus({ minutes: mins })
    .toFormat(SQL_UTC);
}

/**
 * Is the device currently suppressed?
 *
 * Null, empty and unparseable all read **false**. Treating a malformed value as
 * "not muted" matches how a comparison-based backend would treat it, so the UI
 * agrees with the system rather than claiming a state nothing will honour — and
 * the user still gets a working Mute control instead of a stuck banner.
 *
 * @param {string|null|undefined} awayUntil — UTC 'YYYY-MM-DD HH:MM:SS'
 * @param {number} [nowMs] — epoch ms; injectable for tests
 * @returns {boolean}
 */
export function isMuted(awayUntil, nowMs = Date.now()) {
  const ms = muteEndsAtMs(awayUntil);
  return ms !== null && ms > nowMs;
}

/**
 * `away_until` as epoch ms, or null when absent/unparseable.
 *
 * Parsed as UTC explicitly — without `{ zone: 'utc' }` luxon reads a naive SQL
 * string in the browser's zone, which would shift the expiry by the viewer's own
 * offset and make the mute end at the wrong time for anyone outside UTC.
 *
 * @param {string|null|undefined} awayUntil
 * @returns {number|null}
 */
export function muteEndsAtMs(awayUntil) {
  if (!awayUntil || typeof awayUntil !== 'string' || !awayUntil.trim()) return null;
  let dt = DateTime.fromSQL(awayUntil.trim(), { zone: 'utc' });
  if (!dt.isValid) dt = DateTime.fromISO(awayUntil.trim(), { zone: 'utc' });
  return dt.isValid ? dt.toMillis() : null;
}
